import React from "react";
import dayjs from "dayjs";
import DatePicker from "react-datepicker";
import SubHeader from "components/common/SubHeader";
import { useSnackbar } from "contexts/SnackbarContext";
import useCreateClassForm from "hooks/useCreateClassForm";
import {
    PREMIUM_FIXED_PRICE,
    PREMIUM_DEFAULT_SUB_TITLE,
    PREMIUM_SUB_TITLE_MAX_LENGTH,
    formatPriceKRW,
} from "constants/lessonRequest";
import useUserStore from "@store/useUserStore";
import WarningBanner from "components/common/WarningBanner";
import LoadingSpinner from "components/common/LoadingSpiner";
import "./CreateClass.scss";

export const TICKET_TYPES = {
    REGULAR:"정규 수강권",
    SPECIAL_111: "111 CLASS",
    SPECIAL_11: "ELEVEN CLASS",
    SPECIAL_1: "ONE CLASS",
    ONE_DAY: "체험(1회)",
    GENERAL_1T10: "특강",
};

export const SPECIAL_CLASSES = [
    "이퀄라이징(제한수역)",
    "이퀄라이징(개방수역)",
    "덕다이빙 및 피닝 교정",
    "다이나믹",
    "수중촬영",
    "노핀",
    "24m 딥탱크",
    "36m 딥탱크",
    "생존수영",
    "입영 기초",
    "입영 교정",
    "수영",
    "FRC&마우스필",
    "어린이 스컬링",
    "어린이 노핀",
    "어린이 노수모/아웃핏",
    "노수모/아웃핏 수중영상",
];

const CreateClass = () => {
    const userInfo = useUserStore((state) => state.userInfo);
    const isAdmin = userInfo?.is_admin === true;

    const { showSuccess, showError } = useSnackbar();
    const {
        // Refs
        isLoading,
        startTimePickerRef,
        endTimePickerRef,
        datePickerRef,
        // 상태
        classType,
        setClassType,
        setPracticeSport,
        formData,
        dataState,
        isEditMode,
        hasReservations,
        confirmedCount,
        pricingType,
        isSaving,
        practiceCategory,
        setPracticeCategory,
        datePickerState,
        setDatePickerState,
        selectedDates,
        showTimePickerState,
        setShowTimePickerState,
        associationList,
        // 메모이제이션된 데이터
        timeSlots,
        uniqueCourses,
        // 핸들러
        handleInputChange,
        handleCourseChange,
        handleTicketTypeChange,
        toggleDatePicker,
        handleDatesChange,
        removeSelectedDate,
        toggleTimePicker,
        handleSave,
        handleEdit,
        handleDelete,
        handleTitleChange,
        handleAssociationChange,
        selectedAssociations,
        // 상수
        PRACTICE_SPORTS,
        PRACTICE_CATEGORIES,
    } = useCreateClassForm({ showSuccess, showError });

    // 생성: 분류 select 로 formData.pricing_type 설정 / 수정: 상세 응답의 pricing_type
    const isPremiumPractice =
        classType === "practice" &&
        (formData.pricing_type === "PAID" || pricingType === "PAID");

    if (isLoading) return <LoadingSpinner />;

    return (
        <div className="page-create-class">
          <SubHeader title="스케쥴 개설" />
            <div className="warning-section">
              {isAdmin && (
                <WarningBanner message="관리자 권한인 경우 강사명 변경이 가능합니다." />
              )}
              {isEditMode && hasReservations && (
                <WarningBanner message={`예약자가 있어 정원만 수정 가능합니다.`} />
              )}
              {isEditMode && classType === "practice" && pricingType === "PAID" && (
                <WarningBanner message="유료 연습반입니다." />
              )}
            </div>
        
            <div className="input-wrap" style={{padding: '0 20px'}}>
                {/* Class type radio */}
                <div className="radio-input-wrap input-form">
                    <div className="radio-group">
                        <input
                            type="radio"
                            id="lesson"
                            value="lesson"
                            className="custom-radio-input"
                            checked={classType === "lesson"}
                            onChange={(e) => setClassType(e.target.value)}
                            disabled={isEditMode}
                        />
                        <label
                            htmlFor="lesson"
                            className={`custom-radio-label ${
                                classType === "lesson" ? "active" : ""
                            }`}
                        >
                            수업
                        </label>
                        <input
                            type="radio"
                            id="practice"
                            value="practice"
                            className="custom-radio-input"
                            checked={classType === "practice"}
                            onChange={(e) => setClassType(e.target.value)}
                            disabled={isEditMode}
                        />
                        <label
                            htmlFor="practice"
                            className={`custom-radio-label ${
                                classType === "practice" ? "active" : ""
                            }`}
                        >
                            연습반
                        </label>
                    </div>
                </div>

                {/* Instructor */}
                <div className="input-form">
                    <label className="label">강사명</label>
                    <input
                        className={`input ${isAdmin && !(isEditMode && hasReservations) ? "" : "disabled"}`}
                        value={formData.instructor}
                        disabled={!isAdmin || (isEditMode && hasReservations)}
                        onChange={(e) =>
                            handleInputChange("instructor", e.target.value)
                        }
                    />
                </div>

                {/* Course type */}
                {classType === "lesson" ? (
                    <div className="input-form select-input-wrap">
                        <label className="label">강습 종류</label>
                        <select
                            className="input"
                            value={formData.lesson_product}
                            onChange={handleCourseChange}
                            disabled={isEditMode && hasReservations}
                        >
                            <option value="">과정 선택</option>
                            {uniqueCourses.map(({ brand, displayName }) => (
                                <option key={`course-${brand}-${displayName}`} value={displayName}>
                                    {`[${brand}] ${displayName}`}
                                </option>
                            ))}
                        </select>
                    </div>
                ) : (
                    <div className="input-row-2col">
                        <div className="input-form select-input-wrap">
                            <label className="label">종목</label>
                            <select
                                className="input"
                                value={formData.lesson_product}
                                onChange={handleCourseChange}
                                disabled={isEditMode}
                            >
                                <option value="">종목 선택</option>
                                {PRACTICE_SPORTS.map((sport) => (
                                    <option key={`sport-${sport}`} value={sport}>
                                        {sport}
                                    </option>
                                ))}
                            </select>
                        </div>
                        <div className="input-form select-input-wrap">
                            <label className="label">분류</label>
                            <select
                                className="input"
                                value={practiceCategory}
                                onChange={(e) => {
                                    const category = e.target.value;
                                    setPracticeCategory(category);
                                    setPracticeSport(category);
                                    if (category === "프리미엄 트레이닝") {
                                        handleInputChange("pricing_type", "PAID");
                                        handleInputChange("practice_type", "REGULAR_TRAINING");
                                    } else {
                                        handleInputChange("pricing_type", "FREE");
                                        handleInputChange("practice_type", "SELF_PRACTICE");
                                    }
                                }}
                                disabled={isEditMode}
                            >
                                <option value=""> 선택</option>
                                {PRACTICE_CATEGORIES[formData.lesson_product]?.map(
                                    (sport) => (
                                        <option key={`sport-${sport}`} value={sport}>
                                            {sport}
                                        </option>
                                    )
                                )}
                            </select>
                        </div>
                    </div>
                )}

                {/* 프리미엄 트레이닝 자동 유료 안내 문구 */}
                {classType === "practice" && formData.pricing_type === "PAID" && (
                    <div className="info-text" style={{ fontSize: 13, color: '#7c4dff', marginTop: 4, marginBottom: 8 }}>
                        프리미엄 트레이닝은 유료({formatPriceKRW(PREMIUM_FIXED_PRICE)})로 자동 설정됩니다.
                    </div>
                )}

                {/* 프리미엄 트레이닝 부제목 (예약자가 있어도 수정 가능) */}
                {isPremiumPractice && (
                    <div className="input-form">
                        <label className="label" htmlFor="premium-sub-title">
                            프리미엄 트레이닝 부제목
                            <span className="label-hint">
                                {` (${(formData.sub_title || "").length}/${PREMIUM_SUB_TITLE_MAX_LENGTH})`}
                            </span>
                        </label>
                        <input
                            id="premium-sub-title"
                            className="input"
                            value={formData.sub_title || ""}
                            maxLength={PREMIUM_SUB_TITLE_MAX_LENGTH}
                            placeholder={PREMIUM_DEFAULT_SUB_TITLE}
                            onChange={(e) =>
                                handleInputChange("sub_title", e.target.value)
                            }
                        />
                    </div>
                )}

                {/* Ticket type */}
                <div className="input-form select-input-wrap" style={{display:'none'}}>
                    <label className="label">허용 수강권 종류</label>
                    <select
                        className="input disabled"
                        value={formData.allowed_ticket_types}
                        onChange={handleTicketTypeChange}
                        disabled
                    >
                        <option value="">수강권 선택</option>
                        {Object.keys(TICKET_TYPES).map((type) => (
                            <option key={`ticket-${type}`} value={type}>
                                {TICKET_TYPES[type]}
                            </option>
                        ))}
                    </select>
                </div>

                {/* Special class type */}
                {classType === "lesson" &&
                    formData.allowed_ticket_types === "GENERAL_1T10" && (
                        <div className="input-form select-input-wrap">
                            <label className="label">
                                특강 종류(허용 수강권이 "특강"일 경우 선택)
                            </label>
                            <input
                              className={`input`}
                              value={formData.title || ''}
                              onChange={(e) =>
                                  handleInputChange("title", e.target.value)
                              
                              }
                          />
                        </div>
                    )}

                {/* Date + Capacity — 2-column row */}
                <div className="input-row-2col">
                    <div className="input-form">
                        <label className="label">
                            수업 일정
                            {!isEditMode && classType === "lesson" && (
                                <span className="label-hint"> (여러 날짜 선택 가능)</span>
                            )}
                        </label>

                        {!isEditMode && classType === "lesson" ? (
                            // 강습 일괄 개설: 다중 날짜 선택 + 칩
                            <>
                                <div
                                    className="input date-picker"
                                    onClick={toggleDatePicker}
                                >
                                    {selectedDates.length > 0
                                        ? `${selectedDates.length}개 날짜 선택됨`
                                        : "날짜 선택"}
                                </div>
                                {datePickerState && (
                                    <div
                                        className="sportsinput-custom-modal"
                                        ref={datePickerRef}
                                    >
                                        <DatePicker
                                            selectedDates={selectedDates}
                                            selectsMultiple
                                            onChange={handleDatesChange}
                                            inline
                                        />
                                    </div>
                                )}
                                {selectedDates.length > 0 && (
                                    <div className="selected-dates-chips">
                                        {[...selectedDates]
                                            .sort((a, b) => a - b)
                                            .map((d) => {
                                                const key =
                                                    dayjs(d).format("YYYY-MM-DD");
                                                return (
                                                    <span
                                                        key={key}
                                                        className="date-chip"
                                                    >
                                                        {dayjs(d).format("M/D")}
                                                        <button
                                                            type="button"
                                                            className="date-chip-remove"
                                                            onClick={() =>
                                                                removeSelectedDate(d)
                                                            }
                                                            aria-label={`${dayjs(d).format("M월 D일")} 선택 해제`}
                                                        >
                                                            ×
                                                        </button>
                                                    </span>
                                                );
                                            })}
                                    </div>
                                )}
                            </>
                        ) : (
                            // 연습반 / 수정: 단일 날짜
                            <>
                                <div
                                    className={`input date-picker${isEditMode && hasReservations ? " disabled" : ""}`}
                                    onClick={isEditMode && hasReservations ? undefined : toggleDatePicker}
                                    style={{ pointerEvents: isEditMode && hasReservations ? "none" : undefined }}
                                >
                                    {formData.date
                                        ? formData.date.toLocaleDateString()
                                        : "날짜 선택"}
                                </div>
                                {datePickerState && (
                                    <div className="sportsinput-custom-modal" ref={datePickerRef}>
                                        <DatePicker
                                            selected={formData.date}
                                            onChange={(date) => {
                                                handleInputChange("date", date);
                                                setDatePickerState(false);
                                            }}
                                            inline
                                        />
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                    <div className="input-form select-input-wrap">
                        <label className="label">강습 최대인원</label>
                        <select
                            className="input"
                            value={formData.capacity}
                            onChange={(e) =>
                                handleInputChange("capacity", Number(e.target.value))
                            }
                        >
                            {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10]
                                .filter((num) => !hasReservations || num >= confirmedCount)
                                .map((num) => (
                                    <option key={num} value={num}>
                                        {num}명
                                    </option>
                                ))}
                        </select>
                    </div>
                </div>

                {/* Start + End time — 2-column row */}
                <div className="input-row-2col">
                    <div className="input-form">
                        <label className="label">시작 시간</label>
                        <div
                            className={`input date-picker${isEditMode && hasReservations ? " disabled" : ""}`}
                            onClick={isEditMode && hasReservations ? undefined : () => toggleTimePicker("start")}
                            style={{ pointerEvents: isEditMode && hasReservations ? "none" : undefined }}
                        >
                            {formData.start_time
                                ? formData.start_time.toLocaleTimeString([], {
                                      hour: "2-digit",
                                      minute: "2-digit",
                                  })
                                : "시작 시간"}
                        </div>
                        {showTimePickerState.start && (
                            <div className="sportsinput-custom-modal" ref={startTimePickerRef}>
                                <DatePicker
                                    selected={formData.start_time}
                                    onChange={(time) => {
                                        handleInputChange("start_time", time);
                                        setShowTimePickerState({
                                            ...showTimePickerState,
                                            start: false,
                                        });
                                    }}
                                    showTimeSelect
                                    showTimeSelectOnly
                                    includeTimes={timeSlots}
                                    timeCaption="시간"
                                    dateFormat="hh:mm"
                                    inline
                                />
                            </div>
                        )}
                    </div>
                    <div className="input-form">
                        <label className="label">종료 시간</label>
                        <div
                            className={`input date-picker${isEditMode && hasReservations ? " disabled" : ""}`}
                            onClick={isEditMode && hasReservations ? undefined : () => toggleTimePicker("end")}
                            style={{ pointerEvents: isEditMode && hasReservations ? "none" : undefined }}
                        >
                            {formData.end_time
                                ? formData.end_time.toLocaleTimeString([], {
                                      hour: "2-digit",
                                      minute: "2-digit",
                                  })
                                : "종료 시간"}
                        </div>
                        {showTimePickerState.end && (
                            <div className="sportsinput-custom-modal" ref={endTimePickerRef}>
                                <DatePicker
                                    selected={formData.end_time}
                                    onChange={(time) => {
                                        handleInputChange("end_time", time);
                                        setShowTimePickerState({
                                            ...showTimePickerState,
                                            end: false,
                                        });
                                    }}
                                    showTimeSelect
                                    showTimeSelectOnly
                                    includeTimes={timeSlots}
                                    timeCaption="시간"
                                    dateFormat="hh:mm"
                                    inline
                                />
                            </div>
                        )}
                    </div>
                </div>

                {/* Location */}
                <div className="input-form select-input-wrap">
                    <label className="label">장소</label>
                    <select
                        className="input"
                        value={formData.location}
                        onChange={(e) =>
                            handleInputChange("location", e.target.value)
                        }
                        disabled={isEditMode && hasReservations}
                    >
                        <option value="">장소 선택</option>
                        {dataState.locations.map((location) => (
                            <option key={`location-${location}`} value={location}>
                                {location}
                            </option>
                        ))}
                    </select>
                </div>

                {/* Session (day_order) - 강습에서만 표시 */}
                {classType !== "practice" && (
                    <div className="input-form select-input-wrap">
                        <label className="label">
                            세션 (커리큘럼 중 해당하는 회차 기입, Day1, Day2...)
                        </label>
                        <select
                            className="input"
                            value={formData.day_order}
                            onChange={(e) => {
                                if (isNaN(Number(e.target.value)) === false) {
                                    handleInputChange("day_order", e.target.value);
                                } else {
                                    handleInputChange("day_order", null);
                                }
                            }}
                            disabled={isEditMode && hasReservations}
                        >
                            <option value={null}>-</option>
                            {[1, 2, 3, 4, "체험/특강/이론"].map((num) => (
                                <option key={num} value={num}>
                                    {typeof num === "number" ? `${num}강` : num}
                                </option>
                            ))}
                        </select>
                    </div>
                )}

                {/* Association buttons - 강습에서만 표시 */}
                {classType !== "practice" && (
                    <div className="input-form">
                        <label className="label">발급 가능 자격증</label>
                        <div className="association-btn-group">
                            {associationList.map((association) => (
                                <button
                                    key={association.id}
                                    type="button"
                                    className={`association-btn${
                                        selectedAssociations.includes(association.id)
                                            ? " active"
                                            : ""
                                    }`}
                                    onClick={() =>
                                        handleAssociationChange(
                                            association.id,
                                            !selectedAssociations.includes(association.id)
                                        )
                                    }
                                >
                                    {association.name}
                                </button>
                            ))}
                        </div>
                    </div>
                )}

                {/* Instructor comment */}
                <div className="input-form instructor-comment-wrap">
                    <label className="label">강사 안내사항</label>
                    <textarea
                        className="input"
                        placeholder='강습 내용, 잠수풀 안내사항 등 수업에 필요한 정보를 기입해주세요'
                        value={classType === 'lesson'? formData.instructor_comment : formData.note}
                        onChange={(e) => {
                          if(classType === 'lesson'){
                            handleInputChange("instructor_comment", e.target.value);
                          }else{
                            handleInputChange("note", e.target.value);
                          }
                            
                        }}
                        onKeyDown={(e) => {
                            if (e.key === "Enter") {
                                e.stopPropagation();
                            }
                        }}
                        disabled={isEditMode && hasReservations}
                    />
                </div>

                {/* Theory checkbox */}
                {classType !== "practice" && (
                    <div className="checkbox-input-wrap">
                        <p className="label">이론 수업입니다</p>
                        <input
                            className="checkbox-input"
                            type="checkbox"
                            checked={formData.is_theory}
                            onChange={() =>
                                handleInputChange("is_theory", !formData.is_theory)
                            }
                            disabled={isEditMode && hasReservations}
                        />
                    </div>
                )}

                {/* Practice simultaneous creation checkbox */}
                {classType !== "practice" && (
                    <div className="checkbox-input-wrap">
                        <p className="label">연습반 동시 개설</p>
                        <input
                            className="checkbox-input"
                            type="checkbox"
                            checked={formData.create_practice_session}
                            onChange={() =>
                                handleInputChange(
                                    "create_practice_session",
                                    !formData.create_practice_session
                                )
                            }
                            disabled={isEditMode}
                        />
                    </div>
                )}

                {/* Practice capacity + note (conditional) */}
                {formData.create_practice_session && (
                    <>
                        <div className="input-form select-input-wrap">
                            <label className="label">연습반 최대인원</label>
                            <select
                                className="input"
                                value={formData.practice_capacity}
                                onChange={(e) =>
                                    handleInputChange(
                                        "practice_capacity",
                                        Number(e.target.value)
                                    )
                                }
                            >
                                {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((num) => (
                                    <option key={num} value={num}>
                                        {num}
                                    </option>
                                ))}
                            </select>
                        </div>
                        {/* 서버에서 note로 보내는데 반영을 못하는 것 같음 */}
                        {/* <div className="input-form instructor-comment-wrap">
                            <label className="label">연습반 강사 안내사항</label>
                            <textarea
                                className="input"
                                value={formData.note || ""}
                                placeholder='연습반 참여 시 유의사항을 작성해주세요'
                                onChange={(e) =>
                                    handleInputChange("note", e.target.value)
                                }
                                onKeyDown={(e) => {
                                    if (e.key === "Enter") e.stopPropagation();
                                }}
                            />
                        </div> */}
                    </>
                )}

                {/* Save button */}
                <div className="sportsinput-div-input-save">
                    <button
                        className="save-button"
                        onClick={isEditMode ? handleEdit : handleSave}
                        disabled={isSaving}
                    >
                        {isSaving ? "저장 중..." : isEditMode ? "수정" : "저장"}
                    </button>
                    {isEditMode && <button
                        className="del-button"
                        onClick={handleDelete}
                        disabled={isSaving}
                    >
                        삭제
                    </button>}
                </div>
            </div>
        </div>
    );
};

export default CreateClass;
